<script setup>

// Vue 3.2 imports each usage of the hooks and reactivity through ref
import {ref, provide, onMounted, computed} from 'vue';
import {useStore} from 'vuex';

// App.vue specific components
import {useRoute, useRouter} from 'vue-router';
import LeftSlideMenu from "@/components/common/navigation/LeftSlideMenu";
import TopMenu from "@/components/common/navigation/TopMenu";
import NotificationBanner from "@/components/common/other/NotificationBanner";


// Provided globally to all Components through injection
import {SideMenuItems} from "@/classes/component-classes/SideMenuItems";
import ValidationViewModel from "@/classes/global/ValidationViewModel";
import Validation from "@/classes/global/Validation";
import Shipments from "@/classes/viewmodels/Shipments";
import FreightBooking from "@/classes/viewmodels/FreightBooking";
import CourierBooking from "@/classes/viewmodels/CourierBooking";
import PreJobAirBooking from "@/classes/viewmodels/PreJobAirBooking";
import PreJobSeaBooking from "@/classes/viewmodels/PreJobSeaBooking";
import PreJobRoadBooking from "@/classes/viewmodels/PreJobRoadBooking";
import PreJobRailBooking from "@/classes/viewmodels/PreJobRailBooking";
import StockSearch from "@/classes/viewmodels/StockSearch";
import PickStock from "@/classes/viewmodels/PickStock";
import SearchPurchaseOrder from "@/classes/viewmodels/SearchPurchaseOrder"
import PurchaseOrder from "@/classes/viewmodels/PuchaseOrder";
import SkuHistory from "@/classes/viewmodels/SkuHistory";
import BrowsePicks from "@/classes/viewmodels/BrowsePicks";
import Quote from "@/classes/viewmodels/Quote";
import Documents from "@/classes/viewmodels/Documents";
import Reports from "@/classes/viewmodels/Reports";

const route = useRoute();
const router = useRouter();
const store = useStore();

const getHeaderLogo = computed(() => getHeaderLogoPerSection());
const validationViewModel = new ValidationViewModel();
const shipments = new Shipments();
const freightBooking = new FreightBooking();
const courierBooking = new CourierBooking();
const preJobAirBooking = new PreJobAirBooking();
const preJobSeaBooking = new PreJobSeaBooking();
const preJobRoadBooking = new PreJobRoadBooking();
const preJobRailBooking = new PreJobRailBooking();
const stockSearch = new StockSearch();
const pickStock = new PickStock();
const searchPurchaseOrder = new SearchPurchaseOrder();
const purchaseOrder = new PurchaseOrder();
const skuHistory = new SkuHistory();
const browsePicks = new BrowsePicks();
const quote = new Quote();
const documents = new Documents();
const reports = new Reports();

// Injection process
provide("sideMenuItems", ref(new SideMenuItems));
provide("validationViewModel", ref(validationViewModel));
provide("shipments", ref(shipments));
provide("freightBooking", ref(freightBooking));
provide("courierBooking", ref(courierBooking));
provide("preJobAirBooking", ref(preJobAirBooking));
provide("preJobSeaBooking", ref(preJobSeaBooking));
provide("preJobRoadBooking", ref(preJobRoadBooking));
provide("preJobRailBooking", ref(preJobRailBooking));
provide("stockSearch", ref(stockSearch));
provide("pickStock", ref(pickStock));
provide("searchPurchaseOrder", ref(searchPurchaseOrder));
provide("purchaseOrder", ref(purchaseOrder));
provide("skuHistory", ref(skuHistory));
provide("browsePicks", ref(browsePicks));
provide("quote", ref(quote));
provide("documents", ref(documents));
provide("reports", ref(reports));


// Colour Theme Picker - demo
onMounted(async () => {
  document.documentElement.className = 'default'
  // Leave this as a reminder we can perform methods on the router after isReady
  // await router.isReady();

})

function getHeaderLogoPerSection() {

  if (route.name === 'errorPage' || route.name === 'routeGuardErrorPage' || route.name === 'jobNotEditableUserInfo')
    return require(`./assets/error_logo.png`);
  if (route.name === 'noowner') return require(`./assets/error_logo.png`);
  if (route.name === 'dashboard') return require(`./assets/dashboard_logo.png`);
  if (route.name === 'showshipments' || route.name === 'home') return require(`@/assets/shipments/shipments_logo.png`);
  if (route.name === 'viewshipment') return require(`@/assets/shipments/view_shipments_logo.png`);
  if (route.name === "home") return require(`@/assets/shipments/view_shipments_logo.png`);
  if (route.name === 'prejob') return require(`@/assets/bookings/pre_job_logo.png`);
  if (route.name === 'stock') return require(`@/assets/warehouse/stock_logo.png`);
  if (route.name === 'pickstock') return require(`@/assets/warehouse/warehouse-pick_logo.png`);
  if (route.name === 'warehousejobs') return require(`@/assets/warehouse/warehouse-jobs_logo.png`);
  if (route.name === 'skuhistory') return require(`@/assets/warehouse/sku-history_logo.png`);
  if (route.params.section === 'freightBooking') return require(`@/assets/bookings/freight_booking_logo.png`);
  if (route.params.section === 'courierBooking') return require(`@/assets/bookings/courier_booking_logo.png`);
  if (route.name === 'purchaseorder' && route.params.id === 'new') return require(`@/assets/orders/purchase_order_logo.png`);
  if (route.name === 'purchaseorder' && route.params.id === 'browse') return require(`@/assets/orders/browse_orders_logo.png`);
  if (route.name === 'quote' && route.params.id === 'new') return require(`@/assets/quotes/quote_entry_logo.png`);
  if (route.name === 'quote' && route.params.id === 'browse') return require(`@/assets/quotes/browse_quotes_logo.png`);
  if (route.name === 'documents') return require(`@/assets/documents/documents_logo.png`);
  if (route.name === 'reports') return require(`@/assets/reports/reports_logo.png`);
  if (route.name === 'browsepick') return require(`@/assets/warehouse/browse-picks_logo.png`);

}

function getOwnerLogo() {

  try {

      return require(`@/assets/${store.state.owner}/logo.png`);

  } catch (error) {

    return require(`@/assets/boxtrax_logo.png`);
  }

}

function getAccountLogo() {

  try {

      return require(`@/assets/${store.state.owner}/${store.state.userSettings.account}/logo.png`);

  } catch (error) {

    return require(`@/assets/no_logo.png`);
  }

}

</script>
<template id="Vue-3-view-component-App.vue">
  <div class="notification-wrapper">
    <NotificationBanner
        :key="store.state.banners.error.id"
        heading="An Error Has Occured"
        content="Fail"
        type="error"
        v-model="store.state.banners.error">
    </NotificationBanner>
    <NotificationBanner
        :key="store.state.banners.success.id"
        heading="Success"
        content="Fail"
        type="success"
        v-model="store.state.banners.success">
    </NotificationBanner>
    <NotificationBanner
        :key="store.state.banners.warning.id"
        heading="Warning"
        content="Fail"
        type="warning"
        v-model="store.state.banners.warning">
    </NotificationBanner>
  </div>
  <div id="Vue-3-app-vue" class="logo-containers">
    <div style="padding: 4px 0 0 10px;">
      <img alt="owner-logo" :src="getOwnerLogo()">
    </div>
    <div style="margin-left: -180px;">
      <img alt="account-logo" :src="getAccountLogo()">
    </div>
    <div>
      <img alt="Powered By Boxtrax Vision" src="./assets/powered_by_boxtrax_vision.png">
    </div>
  </div>
  <div class="app-header">
    <TopMenu v-if="store.state.owner"></TopMenu>
    <div class="app-location-banner"><img alt="logo" :src="getHeaderLogo"></div>
  </div>
  <LeftSlideMenu></LeftSlideMenu>
  <div class="app-content-wrapper">
    <router-view/>
  </div>
</template>

<style>

@import './css/theme-pallette.scss';
@import './css/global-styling.scss';

body {
  margin: 0;
  background-color: var(--body-bg);
  font-family: 'Roboto', Arial;
  //user-select: none;
  background-size: cover;
}

#app {
  margin: 0;
  display: flex;
  max-width: 1900px;
  flex-direction: column;
  margin: auto auto;
}

.app-header {
  position: relative;
  /*background-image: linear-gradient(0deg, #f3f3f3, #ffffff);*/
  background-color: #fff;
  height: 32px;
  border: 1px solid #d7d7d7;
  border-radius: 6px;
  margin-left: 55px;
  padding: 7px;
}

.app-login-welcome {
  position: absolute;
  font-size: 14px;
  top: 8px;
  left: 1400px;
  color: #3f9968;
}

.app-location-banner {
  position: absolute;
  top: 5px;
  left: 18px;
}

.app-content-wrapper {
  padding-left: 55px;
}

.logo-containers {
  position: relative;
  height: 55px;
  display: flex;
  justify-content: space-between;
  border: 1px solid var(--border-color);
  background-color: #fff;
  margin-left: 55px;
  user-select: none;
  overflow: visible;
  border-radius: var(--rounded-corners);
  margin-bottom: 4px;
  align-items: center;
}

.logo-containers:first-child {
  padding-left: 10px;
}

.logo-containers div:last-of-type {
  padding-right: 30px;
}

@media screen and (max-width: 1325px) {
  .app-location-banner {
    visibility: hidden;
  }

}


</style>
